// src/services/categoryAPI.ts

// 📊 Centralized logging
import { logger } from '../utils/logger';

import { 
  CategoryHierarchy, 
  CreateCategoryRequest, 
  UpdateCategoryRequest, 
  CategoryAPIResponse, 
  CategoryTreeNode,
  CategoryStats,
  CategoryConfiguration,
  CategoryOperationResult
} from '../types/category';
import { tenantConfigAPI } from './tenantConfigAPI';

// Create service-specific logger
const categoryLogger = logger.createServiceLogger('CategoryAPI');

// Default parent categories for new tenants - now with professional icons
const DEFAULT_PARENT_CATEGORIES: CategoryHierarchy[] = [
  { 
    categoryId: 'firearms', 
    name: 'Firearms', 
    icon: 'target', 
    description: 'Duty weapons and firearm accessories',
    parentCategoryId: null,
    sortOrder: 1,
    isActive: true
  },
  { 
    categoryId: 'protective-gear', 
    name: 'Protective Gear', 
    icon: 'shield', 
    description: 'Body armor and protective equipment',
    parentCategoryId: null,
    sortOrder: 2,
    isActive: true
  },
  { 
    categoryId: 'communications', 
    name: 'Communications', 
    icon: 'radio', 
    description: 'Radios, earpieces, and communication equipment',
    parentCategoryId: null,
    sortOrder: 3,
    isActive: true
  },
  { 
    categoryId: 'duty-belt', 
    name: 'Duty Belt Equipment', 
    icon: 'briefcase', 
    description: 'Holsters, handcuffs, OC spray, batons',
    parentCategoryId: null,
    sortOrder: 4,
    isActive: true
  },
  { 
    categoryId: 'uniforms', 
    name: 'Uniforms', 
    icon: 'briefcase', 
    description: 'Uniform items and accessories',
    parentCategoryId: null,
    sortOrder: 5,
    isActive: true
  },
  { 
    categoryId: 'vehicles', 
    name: 'Vehicle Equipment', 
    icon: 'car', 
    description: 'Emergency equipment and vehicle accessories',
    parentCategoryId: null,
    sortOrder: 6,
    isActive: true
  },
  { 
    categoryId: 'medical', 
    name: 'Medical Supplies', 
    icon: 'cross', 
    description: 'First aid and medical equipment',
    parentCategoryId: null,
    sortOrder: 7,
    isActive: true
  },
  { 
    categoryId: 'training', 
    name: 'Training Equipment', 
    icon: 'target', 
    description: 'Training aids and educational materials',
    parentCategoryId: null,
    sortOrder: 8,
    isActive: true
  },
  { 
    categoryId: 'tools', 
    name: 'Tools & Equipment', 
    icon: 'wrench', 
    description: 'Maintenance tools and miscellaneous equipment',
    parentCategoryId: null,
    sortOrder: 9,
    isActive: true
  }
];

class CategoryAPI {
  private readonly CATEGORY_CONFIG_KEY = 'inventory.categories';

  // Generate unique category ID
  private generateCategoryId(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    return `cat_${timestamp}_${random}`;
  }

  // Load categories from tenant configuration
  private async loadCategories(): Promise<CategoryHierarchy[]> {
    try {
      categoryLogger.debug('Loading categories from tenant configuration');
      
      const response = await tenantConfigAPI.getConfiguration(this.CATEGORY_CONFIG_KEY);
      
    if (response.success && response.data?.data?.configuration) {
  const categoriesJson = response.data.data.configuration.values[0];
        if (categoriesJson) {
          const categories: CategoryHierarchy[] = JSON.parse(categoriesJson);
          categoryLogger.info('Categories loaded from configuration', { count: categories.length });
          return categories;
        }
      }
      
      // If no categories exist, return empty array (will trigger initialization)
      categoryLogger.debug('No categories found in configuration');
      return [];
    } catch (error) {
      categoryLogger.error('Error loading categories', undefined, error as Error);
      return [];
    }
  }

  // Save categories to tenant configuration
  private async saveCategories(categories: CategoryHierarchy[]): Promise<boolean> {
    try {
      categoryLogger.debug('Saving categories to tenant configuration', { count: categories.length });
      
      // Store categories as JSON string in the values array
      const categoriesJson = JSON.stringify(categories);
      
      // Check if configuration exists
      const existingResponse = await tenantConfigAPI.getConfiguration(this.CATEGORY_CONFIG_KEY);
      
      if (existingResponse.success && existingResponse.data?.configuration) {
        // Update existing configuration
        const updateResponse = await tenantConfigAPI.updateConfiguration(this.CATEGORY_CONFIG_KEY, {
          displayName: 'Inventory Categories',
          description: 'Hierarchical categories for inventory organization',
          values: [categoriesJson]
        });
        
        if (updateResponse.success) {
          categoryLogger.info('Categories updated successfully');
          return true;
        } else {
          categoryLogger.error('Failed to update categories', { error: updateResponse.error });
          return false;
        }
      } else {
        // Create new configuration
        const createResponse = await tenantConfigAPI.createConfiguration({
          configurationKey: this.CATEGORY_CONFIG_KEY,
          displayName: 'Inventory Categories',
          description: 'Hierarchical categories for inventory organization',
          values: [categoriesJson]
        });
        
        if (createResponse.success) {
          categoryLogger.info('Categories configuration created successfully');
          return true;
        } else {
          categoryLogger.error('Failed to create categories configuration', { error: createResponse.error });
          return false;
        }
      }
    } catch (error) {
      categoryLogger.error('Error saving categories', undefined, error as Error);
      return false;
    }
  }

  // Initialize default categories for new tenants
  private async initializeDefaultCategories(): Promise<CategoryHierarchy[]> {
    categoryLogger.info('Initializing default categories', { count: DEFAULT_PARENT_CATEGORIES.length });
    
    const categories = [...DEFAULT_PARENT_CATEGORIES];
    const success = await this.saveCategories(categories);
    
    if (success) {
      categoryLogger.info('Default categories initialized successfully');
      return categories;
    } else {
      categoryLogger.error('Failed to initialize default categories');
      return [];
    }
  }

  // Calculate stats from categories array
  private calculateStats(categories: CategoryHierarchy[]): CategoryStats {
    const parentCategories = categories.filter(c => !c.parentCategoryId);
    const subcategories = categories.filter(c => c.parentCategoryId);
    const itemsAssigned = categories.reduce((sum, c) => sum + (c.itemCount || 0), 0);
    
    return {
      totalCategories: categories.length,
      parentCategories: parentCategories.length,
      subcategories: subcategories.length,
      itemsAssigned: itemsAssigned,
      itemsUnassigned: 0 // TODO: Calculate from inventory when needed
    };
  }

  // Get all categories for the tenant
  async getAllCategories(): Promise<CategoryAPIResponse<{ categories: CategoryHierarchy[]; stats: CategoryStats }>> {
   try {
      let categories = await this.loadCategories();
      
      // Return empty array if no categories exist
      // Categories should only be initialized during tenant provisioning
      if (categories.length === 0) {
        categoryLogger.warn('No categories found - tenant may need initialization');
      }
      
      // TODO: Calculate item counts by querying inventory
      // For now, set item counts to 0
      categories.forEach(category => {
        category.itemCount = 0;
      });
      
      const stats = this.calculateStats(categories);
      
      return {
        success: true,
        data: {
          categories: categories,
          stats: stats
        }
      };
    } catch (error) {
      categoryLogger.error('Error getting all categories', undefined, error as Error);
      return {
        success: false,
        error: {
          code: 'API_ERROR',
          message: error instanceof Error ? error.message : 'Failed to load categories'
        }
      };
    }
  }

  // Get category tree structure
  async getCategoryTree(): Promise<CategoryAPIResponse<{ tree: CategoryTreeNode[]; stats: CategoryStats }>> {
    try {
      const response = await this.getAllCategories();
      
      if (!response.success || !response.data) {
        return {
          success: false,
          error: response.error || { code: 'DATA_ERROR', message: 'Failed to load categories' }
        };
      }
      
      const tree = this.buildCategoryTree(response.data.categories);
      
      return {
        success: true,
        data: {
          tree: tree,
          stats: response.data.stats
        }
      };
    } catch (error) {
      categoryLogger.error('Error getting category tree', undefined, error as Error);
      return {
        success: false,
        error: {
          code: 'API_ERROR',
          message: error instanceof Error ? error.message : 'Failed to build category tree'
        }
      };
    }
  }

  // Get parent categories only
  async getParentCategories(): Promise<CategoryAPIResponse<{ categories: CategoryHierarchy[] }>> {
    try {
      const response = await this.getAllCategories();
      
      if (!response.success || !response.data) {
        return {
          success: false,
          error: response.error || { code: 'DATA_ERROR', message: 'Failed to load categories' }
        };
      }
      
      const parentCategories = response.data.categories.filter(c => !c.parentCategoryId);
      
      return {
        success: true,
        data: {
          categories: parentCategories
        }
      };
    } catch (error) {
      categoryLogger.error('Error getting parent categories', undefined, error as Error);
      return {
        success: false,
        error: {
          code: 'API_ERROR',
          message: error instanceof Error ? error.message : 'Failed to load parent categories'
        }
      };
    }
  }

  // Get subcategories for a parent
  async getSubcategories(parentCategoryId: string): Promise<CategoryAPIResponse<{ categories: CategoryHierarchy[] }>> {
    try {
      const response = await this.getAllCategories();
      
      if (!response.success || !response.data) {
        return {
          success: false,
          error: response.error || { code: 'DATA_ERROR', message: 'Failed to load categories' }
        };
      }
      
      const subcategories = response.data.categories.filter(c => c.parentCategoryId === parentCategoryId);
      
      return {
        success: true,
        data: {
          categories: subcategories
        }
      };
    } catch (error) {
      categoryLogger.error('Error getting subcategories', { parentCategoryId }, error as Error);
      return {
        success: false,
        error: {
          code: 'API_ERROR',
          message: error instanceof Error ? error.message : 'Failed to load subcategories'
        }
      };
    }
  }

  // Get single category
  async getCategory(categoryId: string): Promise<CategoryAPIResponse<{ category: CategoryHierarchy }>> {
    try {
      const response = await this.getAllCategories();
      
      if (!response.success || !response.data) {
        return {
          success: false,
          error: response.error || { code: 'DATA_ERROR', message: 'Failed to load categories' }
        };
      }
      
      const category = response.data.categories.find(c => c.categoryId === categoryId);
      
      if (!category) {
        return {
          success: false,
          error: {
            code: 'NOT_FOUND',
            message: 'Category not found'
          }
        };
      }
      
      return {
        success: true,
        data: {
          category: category
        }
      };
    } catch (error) {
      categoryLogger.error('Error getting category', { categoryId }, error as Error);
      return {
        success: false,
        error: {
          code: 'API_ERROR',
          message: error instanceof Error ? error.message : 'Failed to load category'
        }
      };
    }
  }

  // Create new category
  async createCategory(categoryRequest: CreateCategoryRequest): Promise<CategoryAPIResponse<{ category: CategoryHierarchy; message: string }>> {
    try {
      const categories = await this.loadCategories();
      
      // Generate new category ID and sort order
      const categoryId = this.generateCategoryId();
      const maxSortOrder = categories.reduce((max, cat) => Math.max(max, cat.sortOrder || 0), 0);
      
      const newCategory: CategoryHierarchy = {
        categoryId: categoryId,
        name: categoryRequest.name,
        description: categoryRequest.description || '',
        icon: categoryRequest.icon,
        parentCategoryId: categoryRequest.parentCategoryId || null,
        sortOrder: categoryRequest.sortOrder || (maxSortOrder + 1),
        isActive: true,
        itemCount: 0
      };
      
      categoryLogger.info('Creating new category', {
        categoryId: newCategory.categoryId,
        name: newCategory.name,
        parentCategoryId: newCategory.parentCategoryId
      });
      
      // Add to categories array
      categories.push(newCategory);
      
      // Save updated categories
      const success = await this.saveCategories(categories);
      
      if (success) {
        return {
          success: true,
          data: {
            category: newCategory,
            message: 'Category created successfully'
          }
        };
      } else {
        return {
          success: false,
          error: {
            code: 'SAVE_ERROR',
            message: 'Failed to save new category'
          }
        };
      }
    } catch (error) {
      categoryLogger.error('Error creating category', { categoryRequest }, error as Error);
      return {
        success: false,
        error: {
          code: 'API_ERROR',
          message: error instanceof Error ? error.message : 'Failed to create category'
        }
      };
    }
  }

  // Update existing category
  async updateCategory(categoryId: string, categoryRequest: UpdateCategoryRequest): Promise<CategoryAPIResponse<{ category: CategoryHierarchy; message: string }>> {
    try {
      const categories = await this.loadCategories();
      
      // Find category to update
      const categoryIndex = categories.findIndex(c => c.categoryId === categoryId);
      
      if (categoryIndex === -1) {
        return {
          success: false,
          error: {
            code: 'NOT_FOUND',
            message: 'Category not found'
          }
        };
      }
      
      // Update category properties
      const updatedCategory = { ...categories[categoryIndex] };
      
      if (categoryRequest.name !== undefined) updatedCategory.name = categoryRequest.name;
      if (categoryRequest.description !== undefined) updatedCategory.description = categoryRequest.description;
      if (categoryRequest.icon !== undefined) updatedCategory.icon = categoryRequest.icon;
      if (categoryRequest.sortOrder !== undefined) updatedCategory.sortOrder = categoryRequest.sortOrder;
      if (categoryRequest.isActive !== undefined) updatedCategory.isActive = categoryRequest.isActive;
      
      categoryLogger.info('Updating category', {
        categoryId,
        fieldsUpdated: Object.keys(categoryRequest)
      });
      
      // Replace in array
      categories[categoryIndex] = updatedCategory;
      
      // Save updated categories
      const success = await this.saveCategories(categories);
      
      if (success) {
        return {
          success: true,
          data: {
            category: updatedCategory,
            message: 'Category updated successfully'
          }
        };
      } else {
        return {
          success: false,
          error: {
            code: 'SAVE_ERROR',
            message: 'Failed to save updated category'
          }
        };
      }
    } catch (error) {
      categoryLogger.error('Error updating category', { categoryId, categoryRequest }, error as Error);
      return {
        success: false,
        error: {
          code: 'API_ERROR',
          message: error instanceof Error ? error.message : 'Failed to update category'
        }
      };
    }
  }

  // Delete category
  async deleteCategory(categoryId: string): Promise<CategoryAPIResponse<{ message: string }>> {
    try {
      const categories = await this.loadCategories();
      
      // Find category to delete
      const categoryToDelete = categories.find(c => c.categoryId === categoryId);
      
      if (!categoryToDelete) {
        return {
          success: false,
          error: {
            code: 'NOT_FOUND',
            message: 'Category not found'
          }
        };
      }
      
      // If deleting a parent category, also delete all children
      const categoriesToDelete = [categoryId];
      if (!categoryToDelete.parentCategoryId) {
        // This is a parent category, find all children
        const children = categories.filter(c => c.parentCategoryId === categoryId);
        categoriesToDelete.push(...children.map(c => c.categoryId));
      }
      
      categoryLogger.warn('Deleting category', {
        categoryId,
        isParent: !categoryToDelete.parentCategoryId,
        totalToDelete: categoriesToDelete.length
      });
      
      // Remove categories from array
      const updatedCategories = categories.filter(c => !categoriesToDelete.includes(c.categoryId));
      
      // Save updated categories
      const success = await this.saveCategories(updatedCategories);
      
      if (success) {
        const deletedCount = categoriesToDelete.length;
        const message = deletedCount === 1 
          ? 'Category deleted successfully'
          : `Category and ${deletedCount - 1} subcategories deleted successfully`;
          
        return {
          success: true,
          data: {
            message: message
          }
        };
      } else {
        return {
          success: false,
          error: {
            code: 'SAVE_ERROR',
            message: 'Failed to save after deleting category'
          }
        };
      }
    } catch (error) {
      categoryLogger.error('Error deleting category', { categoryId }, error as Error);
      return {
        success: false,
        error: {
          code: 'API_ERROR',
          message: error instanceof Error ? error.message : 'Failed to delete category'
        }
      };
    }
  }

  // Reorder categories
  async reorderCategories(categoryUpdates: { categoryId: string; sortOrder: number }[]): Promise<CategoryAPIResponse<{ message: string }>> {
    try {
      const categories = await this.loadCategories();
      
      categoryLogger.info('Reordering categories', { updateCount: categoryUpdates.length });
      
      // Update sort orders
      categoryUpdates.forEach(update => {
        const category = categories.find(c => c.categoryId === update.categoryId);
        if (category) {
          category.sortOrder = update.sortOrder;
        }
      });
      
      // Save updated categories
      const success = await this.saveCategories(categories);
      
      if (success) {
        return {
          success: true,
          data: {
            message: 'Categories reordered successfully'
          }
        };
      } else {
        return {
          success: false,
          error: {
            code: 'SAVE_ERROR',
            message: 'Failed to save reordered categories'
          }
        };
      }
    } catch (error) {
      categoryLogger.error('Error reordering categories', { categoryUpdates }, error as Error);
      return {
        success: false,
        error: {
          code: 'API_ERROR',
          message: error instanceof Error ? error.message : 'Failed to reorder categories'
        }
      };
    }
  }

  // Utility method to build tree structure from flat array
  buildCategoryTree(categories: CategoryHierarchy[]): CategoryTreeNode[] {
    const categoryMap = new Map<string, CategoryTreeNode>();
    const rootCategories: CategoryTreeNode[] = [];

    // First pass: create all nodes
    categories.forEach(category => {
      categoryMap.set(category.categoryId, {
        ...category,
        children: [],
        level: category.parentCategoryId ? 1 : 0
      });
    });

    // Second pass: build tree structure
    categories.forEach(category => {
      const node = categoryMap.get(category.categoryId)!;
      
      if (category.parentCategoryId) {
        // This is a subcategory
        const parent = categoryMap.get(category.parentCategoryId);
        if (parent) {
          parent.children.push(node);
        }
      } else {
        // This is a parent category
        rootCategories.push(node);
      }
    });

    // Sort by sortOrder
    const sortNodes = (nodes: CategoryTreeNode[]) => {
      nodes.sort((a, b) => a.sortOrder - b.sortOrder);
      nodes.forEach(node => sortNodes(node.children));
    };
    
    sortNodes(rootCategories);
    return rootCategories;
  }

  // Get simple category names (for compatibility with existing inventory forms)
  async getSimpleCategoryNames(): Promise<string[]> {
    try {
      const response = await this.getAllCategories();
      
      if (response.success && response.data) {
        // Return only parent category names for simplicity
        return response.data.categories
          .filter(c => !c.parentCategoryId && c.isActive)
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map(c => c.name);
      }
      
      return [];
    } catch (error) {
      categoryLogger.error('Error getting simple category names', undefined, error as Error);
      return [];
    }
  }

  // Reset to default categories (admin operation)
  async resetToDefaults(): Promise<CategoryAPIResponse<{ message: string }>> {
    try {
      categoryLogger.warn('Resetting categories to defaults');
      
      const success = await this.saveCategories(DEFAULT_PARENT_CATEGORIES);
      
      if (success) {
        return {
          success: true,
          data: {
            message: 'Categories reset to default successfully'
          }
        };
      } else {
        return {
          success: false,
          error: {
            code: 'SAVE_ERROR',
            message: 'Failed to reset categories to defaults'
          }
        };
      }
    } catch (error) {
      categoryLogger.error('Error resetting categories', undefined, error as Error);
      return {
        success: false,
        error: {
          code: 'API_ERROR',
          message: error instanceof Error ? error.message : 'Failed to reset categories'
        }
      };
    }
  }
}

// Export singleton instance
export const categoryAPI = new CategoryAPI();